import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";

import { Trash2 } from "lucide-react";
import { GET_PARTNERS } from "../../GraphQL/settings/queries";
import InvitePartnerModal from "./InvitePartnerModal";
import {
  DELETE_PARTNER,
  APPROVE_PARTNER,
} from "../../GraphQL/settings/mutations";
import confirmWithModal from "../Global/confirmWithModal";

interface Props {
  companyId: number;
}

const Partnerships = ({ companyId }: Props) => {
  const { loading, error, data, refetch } = useQuery(GET_PARTNERS);
  const [deletePartner] = useMutation(DELETE_PARTNER);
  const [approvePartner] = useMutation(APPROVE_PARTNER);

  const [showInvitePartnerModal, setShowInvitePartnerModal] = useState(false);

  const handleInvitePartner = () => {
    setShowInvitePartnerModal(true);
  };

  const handleDelete = async (id: string) => {
    const confirmation = await confirmWithModal(
      "Are you sure you want to delete this partnership?"
    );
    if (confirmation) {
      try {
        await deletePartner({ variables: { companyId: id } });
        alert("Partnership deleted");
        refetch();
      } catch (err) {
        alert("Partnership unable to be deleted. Please try again soon");
        console.log(err);
      }
    }
  };

  const handleAccept = async (id: string) => {
    try {
      await approvePartner({ variables: { companyId: id } });
      alert("Partnership approved");
      refetch();
    } catch (err) {
      alert("Something went wrong. Please try again soon");
      console.log(err);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading partnerships</p>;

  return (
    <Card className="border-0">
      <InvitePartnerModal
        show={showInvitePartnerModal}
        onHide={() => setShowInvitePartnerModal(false)}
      />
      <CardContent className="p-0">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <strong>Partner Agencies:</strong>
            {data?.partners?.items?.length === 0 && (
              <p>You are not currently partnered with any agencies</p>
            )}
            <Table>
              <TableBody>
                {data?.partners?.items?.map((partner: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{companyId === partner.companyAId
                                ? partner.companyBName
                                : partner.companyAName}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          partner.status === "PENDING" ? "secondary" : "secondary"
                        }
                      >
                        {partner.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {partner.status === "PENDING" &&
                        partner.companyAId !== companyId && (
                          <Button
                            onClick={() => handleAccept(partner.companyAId)}
                            size="sm"
                          >
                            Accept
                          </Button>
                        )}
                    </TableCell>
                    <TableCell>
                      {partner.status !== "REMOVED" && (
                        <Button
                          onClick={() =>
                            handleDelete(
                              companyId === partner.companyAId
                                ? partner.companyBId
                                : partner.companyAId
                            )
                          }
                          variant="secondary"
                          size="sm"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        <div className="flex justify-end pt-lg border-t border-slate-200 dark:border-slate-700">
          <Button size="sm" onClick={handleInvitePartner}>
            Invite partner
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Partnerships;
